'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';

/* ─── Animation timeline config ─── */
const TYPING_SPEED_MS = 50;

const DELAYS = {
  initialWait: 2000,
  afterModalOpen: 300,
  selectInterval: 400,
  afterSelects: 300,
  afterTypingDone: 500,
  afterModalClose: 500,
  pendingToRunning: 1200,
  runningToDone: 2000,
  afterDone: 800,
  detailView: 2500,
  beforeReset: 2000,
  slashMenuAppear: 200,
  slashMenuSelect: 500,
} as const;

/* ─── Prompt segments ─── */
type PromptSegment =
  | { type: 'text'; value: string }
  | {
      type: 'slash';
      command: string;
      menuItems: { icon: string; label: string }[];
      selectedIndex: number;
      chip: { icon: string; label: string };
    };

const PROMPT_SEGMENTS: PromptSegment[] = [
  { type: 'text', value: 'Implement ' },
  {
    type: 'slash',
    command: '/plan',
    menuItems: [
      { icon: '\u{1F4DD}', label: 'Settings Revamp' },
      { icon: '\u{1F4DD}', label: 'Auth Migration' },
    ],
    selectedIndex: 0,
    chip: { icon: '\u{1F4DD}', label: 'Settings Revamp' },
  },
  { type: 'text', value: ' dark mode toggle' },
];

/* ─── Typing state machine ─── */
type TypingPhase = 'idle' | 'typing-text' | 'typing-slash' | 'slash-menu-visible' | 'slash-menu-selected' | 'done';

type TypingState = {
  /** Rendered elements so far (text strings + chips) */
  rendered: ReactNode[];
  /** Current text being typed char by char */
  currentText: string;
  /** Active slash command being typed */
  slashText: string;
  /** Show slash menu */
  showSlashMenu: boolean;
  /** Slash menu items */
  slashMenuItems: { icon: string; label: string }[];
  /** Selected slash item index */
  slashSelectedIndex: number;
  /** Phase */
  phase: TypingPhase;
};

const initialTypingState: TypingState = {
  rendered: [],
  currentText: '',
  slashText: '',
  showSlashMenu: false,
  slashMenuItems: [],
  slashSelectedIndex: -1,
  phase: 'idle',
};

type AnimStep =
  | 'list'
  | 'fab-pulse'
  | 'modal-open'
  | 'select-runner'
  | 'select-agent'
  | 'select-engine'
  | 'select-model'
  | 'check-options'
  | 'typing'
  | 'sending'
  | 'modal-close'
  | 'new-item-enter'
  | 'status-running'
  | 'status-done'
  | 'detail-enter'
  | 'detail-view'
  | 'cooldown';

/* ─── Static row data ─── */
const existingRows = [
  {
    prompt: 'Implement user auth flow',
    runner: 'Mac Mini',
    engine: 'Codex',
    status: 'Done',
    statusCls: 'bg-success/15 text-success',
    time: '5h',
    priorAttempts: [
      {
        prompt: 'Implement user auth flow',
        engine: 'Antigravity',
        status: 'Done',
        statusCls: 'bg-success/15 text-success',
        time: '7h',
      },
      {
        prompt: 'Implement user auth flow',
        engine: 'Claude',
        status: 'Done',
        statusCls: 'bg-success/15 text-success',
        time: '8h',
      },
    ],
  },
  {
    prompt: 'Prepare co-action handoff',
    runner: 'CI Linux',
    engine: 'Claude',
    status: 'Done',
    statusCls: 'bg-success/15 text-success',
    time: '10h',
  },
];

const statusConfig: Record<string, { label: string; cls: string }> = {
  Pending: { label: 'Pending', cls: 'bg-warning/15 text-warning' },
  Running: { label: 'Running', cls: 'bg-info/15 text-info' },
  Done: { label: 'Done', cls: 'bg-success/15 text-success' },
};

/* ─── Select field config ─── */
type SelectFieldId = 'runner' | 'agent' | 'engine' | 'model';

const selectFields: { id: SelectFieldId; label: string; value: string }[] = [
  { id: 'runner', label: 'Runner', value: 'Mac Mini' },
  { id: 'agent', label: 'Agent', value: 'dev-agent' },
  { id: 'engine', label: 'Engine', value: 'Claude' },
  { id: 'model', label: 'Model', value: 'sonnet-4' },
];

const selectStepMap: Record<SelectFieldId, AnimStep> = {
  runner: 'select-runner',
  agent: 'select-agent',
  engine: 'select-engine',
  model: 'select-model',
};

/* ─── Sub-components ─── */

function StatusBar() {
  return (
    <div className="relative flex items-center justify-between px-5 pb-1 pt-2.5">
      <span className="text-[8px] font-medium text-base-content/30">9:41</span>
      <div className="absolute left-1/2 top-2 h-[14px] w-[60px] -translate-x-1/2 rounded-full bg-neutral" />
      <div className="flex items-center gap-1">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-2.5 w-2.5 text-base-content/30"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <rect x="17" y="4" width="4" height="16" rx="1" />
          <rect x="11" y="7" width="4" height="13" rx="1" />
          <rect x="5" y="10" width="4" height="10" rx="1" />
        </svg>
        <div className="h-2 w-4 rounded-xs border border-base-content/30 p-px">
          <div className="h-full w-3/4 rounded-xs bg-base-content/30" />
        </div>
      </div>
    </div>
  );
}

function AppHeader({ count }: { count: number }) {
  return (
    <div className="border-b border-base-200 px-3.5 py-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5 text-primary" viewBox="0 0 512 512" role="img">
            <defs>
              <linearGradient id="apm-logo-g" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0%" stopColor="#3b82f6" />
                <stop offset="100%" stopColor="#1d4ed8" />
              </linearGradient>
            </defs>
            <rect width="512" height="512" rx="96" fill="url(#apm-logo-g)" />
            <path
              d="M152 384L256 128l104 256"
              fill="none"
              stroke="#fff"
              strokeWidth="40"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <line x1="196" y1="290" x2="316" y2="290" stroke="#fff" strokeWidth="40" strokeLinecap="round" />
          </svg>
          <span className="text-[10px] font-semibold text-base-content/70">Runners</span>
        </div>
        <span className="text-[8px] text-base-content/35">{count} requests</span>
      </div>
    </div>
  );
}

function RunnerRow({
  prompt,
  runner,
  engine,
  status,
  statusCls,
  time,
  priorAttempts,
}: {
  prompt: string;
  runner: string;
  engine: string;
  status: string;
  statusCls: string;
  time: string;
  priorAttempts?: { prompt: string; engine: string; status: string; statusCls: string; time: string }[];
}) {
  return (
    <div className="px-3.5 py-3">
      <div className="flex items-start justify-between gap-2">
        <p className="text-[9px] font-medium leading-snug text-base-content/80 line-clamp-2">{prompt}</p>
        <span
          className={`mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[7px] font-semibold leading-none transition-all duration-300 ${statusCls}`}
        >
          {status}
        </span>
      </div>
      <div className="mt-1.5 flex items-center gap-1.5 text-[7px] text-base-content/40">
        <span>{runner}</span>
        <span className="text-base-content/20">&middot;</span>
        <span>{engine}</span>
        <span className="text-base-content/20">&middot;</span>
        <span>{time}</span>
      </div>
      {priorAttempts && priorAttempts.length > 0 && (
        <div className="mt-1.5 space-y-1">
          {priorAttempts.map((attempt) => (
            <div
              key={attempt.engine + attempt.time}
              className="rounded-lg border border-base-200 bg-base-200/20 px-2 py-1.5"
            >
              <div className="flex items-center justify-between gap-1">
                <div className="flex items-center gap-1">
                  <span className="rounded-full border border-base-300 px-1 py-px text-[5px] font-medium text-base-content/50">
                    Previous
                  </span>
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={
                      attempt.engine === 'Claude'
                        ? '/agents/claude.svg'
                        : attempt.engine === 'Antigravity'
                          ? '/agents/antigravity.svg'
                          : '/agents/openai.svg'
                    }
                    alt={attempt.engine}
                    className={`h-[10px] w-[10px] ${attempt.engine !== 'Claude' && attempt.engine !== 'Antigravity' ? 'dark:invert' : ''}`}
                  />
                  <span className="rounded bg-success/15 px-1 py-px text-[5px] font-semibold text-success">R</span>
                </div>
                <span className={`rounded-full px-1 py-px text-[5px] font-medium ${attempt.statusCls}`}>
                  {attempt.status}
                </span>
              </div>
              <p className="mt-0.5 line-clamp-1 text-[6px] text-base-content/60">{attempt.prompt}</p>
              <p className="mt-0.5 text-[5px] text-base-content/30">{attempt.time}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FabButton({ pulsing }: { pulsing: boolean }) {
  return (
    <div className="relative mt-auto pb-2">
      <div
        className={`absolute bottom-4 right-3 flex h-7 w-7 items-center justify-center rounded-full bg-primary shadow-md transition-transform duration-200 ${pulsing ? 'animate-[fab-pulse_1s_ease-in-out_infinite]' : ''}`}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-3.5 w-3.5 text-primary-content"
          viewBox="0 0 24 24"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M14.615 1.595a.75.75 0 01.359.852L12.982 9.75h7.268a.75.75 0 01.548 1.262l-10.5 11.25a.75.75 0 01-1.272-.71l1.992-7.302H3.818a.75.75 0 01-.548-1.262l10.5-11.25a.75.75 0 01.845-.143z"
            clipRule="evenodd"
          />
        </svg>
      </div>
    </div>
  );
}

function MiniSelect({
  label,
  value,
  filled,
  active,
}: {
  label: string;
  value: string;
  filled: boolean;
  active: boolean;
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[6px] font-medium text-base-content/40">{label}</span>
      <div
        className={`flex h-[18px] items-center rounded border px-1.5 text-[7px] transition-all duration-200 ${
          active
            ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
            : filled
              ? 'border-base-300 bg-base-100'
              : 'border-base-300 bg-base-200/50'
        }`}
      >
        <span className={filled ? 'text-base-content/70' : 'text-base-content/25'}>{filled ? value : 'Select...'}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="ml-auto h-2 w-2 shrink-0 text-base-content/30"
          viewBox="0 0 20 20"
          fill="currentColor"
        >
          <path
            fillRule="evenodd"
            d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z"
            clipRule="evenodd"
          />
        </svg>
      </div>
    </div>
  );
}

function MiniCheckbox({ label, checked }: { label: string; checked: boolean }) {
  return (
    <label className="flex items-center gap-1 cursor-default">
      <div
        className={`flex h-[10px] w-[10px] items-center justify-center rounded-xs border transition-all duration-200 ${checked ? 'border-primary bg-primary' : 'border-base-content/25 bg-base-100'}`}
      >
        {checked && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-[7px] w-[7px] text-primary-content"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        )}
      </div>
      <span
        className={`text-[7px] font-medium transition-colors duration-200 ${checked ? 'text-base-content/70' : 'text-base-content/40'}`}
      >
        {label}
      </span>
    </label>
  );
}

function EntityChip({ icon, label }: { icon: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-0.5 rounded bg-primary/10 px-1 py-px text-[7px] font-medium text-primary">
      <span className="text-[6px]">{icon}</span>
      <span>{label}</span>
    </span>
  );
}

function SlashMenu({ items, selectedIndex }: { items: { icon: string; label: string }[]; selectedIndex: number }) {
  return (
    <div
      className="absolute bottom-full left-0 mb-1 w-[120px] rounded-md border border-base-300 bg-base-100 py-0.5 shadow-lg"
      style={{ animation: 'slash-menu-in 0.15s ease-out' }}
    >
      {items.map((item, i) => (
        <div
          key={item.label}
          className={`flex items-center gap-1 px-2 py-1 text-[7px] ${i === selectedIndex ? 'bg-base-200' : ''}`}
        >
          <span className="text-[8px]">{item.icon}</span>
          <span className="truncate text-base-content/70">{item.label}</span>
        </div>
      ))}
    </div>
  );
}

function ModalOverlay({
  visible,
  filledFields,
  activeField,
  optionsChecked,
  typingState,
  sending,
}: {
  visible: boolean;
  filledFields: Set<SelectFieldId>;
  activeField: SelectFieldId | null;
  optionsChecked: boolean;
  typingState: TypingState;
  sending: boolean;
}) {
  const allFilled = filledFields.size >= selectFields.length;
  const showPrompt = allFilled;
  const hasContent =
    typingState.rendered.length > 0 || typingState.currentText.length > 0 || typingState.slashText.length > 0;

  return (
    <div
      className={`absolute inset-0 z-10 flex flex-col justify-end transition-opacity duration-200 ${visible ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
    >
      <div className="absolute inset-0 rounded-[24px] bg-black/30" />
      <div
        className={`relative z-10 rounded-t-2xl bg-base-100 px-3.5 pb-4 pt-3 transition-transform duration-300 ${visible ? 'translate-y-0' : 'translate-y-full'}`}
      >
        <div className="mb-2.5 flex items-center justify-between">
          <span className="text-[9px] font-semibold text-base-content/70">New Request</span>
          <div className="h-1 w-6 rounded-full bg-base-content/15" />
        </div>

        {/* Select fields */}
        <div className="mb-2.5 grid grid-cols-2 gap-x-2 gap-y-1.5">
          {selectFields.map((field) => (
            <MiniSelect
              key={field.id}
              label={field.label}
              value={field.value}
              filled={filledFields.has(field.id)}
              active={activeField === field.id}
            />
          ))}
        </div>

        {/* Execution options */}
        <div className="mb-2.5 flex items-center gap-3">
          <MiniCheckbox label="Plan Mode" checked={false} />
          <MiniCheckbox label="RunnerBox" checked={optionsChecked} />
        </div>

        {/* Prompt input */}
        <div
          className={`relative mb-2.5 rounded-lg border px-2.5 py-2 transition-all duration-200 ${showPrompt ? 'min-h-[36px] border-base-300' : 'min-h-[24px] border-base-200 bg-base-200/30'}`}
        >
          {showPrompt ? (
            <p className="text-[8px] leading-relaxed text-base-content/70">
              {typingState.rendered.map((node, i) => (
                <span key={i}>{node}</span>
              ))}
              {typingState.currentText}
              {typingState.slashText.length > 0 && <span className="text-primary">{typingState.slashText}</span>}
              <span className="inline-block h-2.5 w-px animate-[cursor-blink_0.8s_step-end_infinite] bg-base-content/60" />
            </p>
          ) : (
            <p className="text-[8px] text-base-content/25">Prompt</p>
          )}
          {/* Slash command menu */}
          {typingState.showSlashMenu && (
            <SlashMenu items={typingState.slashMenuItems} selectedIndex={typingState.slashSelectedIndex} />
          )}
        </div>

        {/* Send button */}
        <button
          type="button"
          className={`flex w-full items-center justify-center rounded-lg py-1.5 text-[8px] font-semibold transition-all duration-200 ${
            sending
              ? 'bg-primary text-primary-content'
              : hasContent
                ? 'bg-primary text-primary-content'
                : 'bg-base-200 text-base-content/30'
          }`}
          tabIndex={-1}
        >
          {sending ? (
            <span className="inline-block h-2.5 w-2.5 animate-spin rounded-full border border-primary-content/30 border-t-primary-content" />
          ) : (
            'Send request'
          )}
        </button>
      </div>
    </div>
  );
}

/* ─── Detail view sub-component ─── */

const detailLogs = [
  { time: '09:41:02', level: 'INFO', levelCls: 'bg-info/15 text-info', msg: 'RunnerBox created: runnerbox/a3f2' },
  { time: '09:41:05', level: 'INFO', levelCls: 'bg-info/15 text-info', msg: 'Agent started on Mac Mini' },
  { time: '09:41:12', level: 'INFO', levelCls: 'bg-info/15 text-info', msg: 'Reading settings module...' },
  { time: '09:42:30', level: 'INFO', levelCls: 'bg-info/15 text-info', msg: 'Dark mode toggle added' },
  { time: '09:42:45', level: 'INFO', levelCls: 'bg-info/15 text-info', msg: 'Task completed successfully' },
];

function DetailBottomSheet({ visible }: { visible: boolean }) {
  return (
    <div
      className={`absolute inset-0 z-20 flex flex-col justify-end transition-opacity duration-200 ${visible ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 rounded-[24px] bg-black/30" />

      {/* Bottom sheet */}
      <div
        className={`relative z-10 flex flex-col rounded-t-2xl bg-base-100 transition-transform duration-300 ${visible ? 'translate-y-0' : 'translate-y-full'}`}
        style={{ height: '86%' }}
      >
        {/* Handle bar */}
        <div className="flex justify-center pt-2 pb-1">
          <div className="h-0.5 w-6 rounded-full bg-base-content/15" />
        </div>

        {/* Header */}
        <div className="border-b border-base-200 px-3 pb-2 flex items-center justify-between">
          <div className="flex items-center gap-1 min-w-0">
            <span className="text-[8px] font-semibold text-base-content/70">Request Details</span>
            <span className="text-[6px] text-base-content/35">2m ago</span>
          </div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-2.5 w-2.5 text-base-content/30 shrink-0"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col min-h-0 px-3 py-2 gap-1.5">
          {/* Status badges */}
          <div className="flex flex-wrap items-center gap-1">
            <span className="rounded-full bg-success/15 px-1.5 py-0.5 text-[6px] font-semibold text-success">Done</span>
            <span className="rounded-full bg-base-200 px-1.5 py-0.5 text-[6px] font-medium text-base-content/60">
              Mac Mini
            </span>
            <span className="rounded-full bg-base-200 px-1.5 py-0.5 text-[6px] font-medium text-base-content/60">
              Claude
            </span>
            <span className="rounded-full bg-base-200 px-1.5 py-0.5 text-[6px] font-medium text-base-content/60">
              sonnet-4
            </span>
          </div>

          {/* RunnerBox */}
          <div className="flex items-center gap-1">
            <span className="text-[5px] font-semibold uppercase tracking-wider text-base-content/35">RunnerBox</span>
            <span className="rounded-full bg-success/15 px-1.5 py-0.5 text-[5px] font-semibold text-success">
              Active
            </span>
            <span className="rounded bg-base-200 px-1 py-0.5 text-[5px] font-mono text-base-content/45">main</span>
          </div>

          {/* Prompt */}
          <div>
            <p className="text-[5px] font-semibold uppercase tracking-wider text-base-content/35 mb-0.5">Prompt</p>
            <div className="rounded border border-base-200 px-1.5 py-1">
              <p className="text-[7px] leading-snug text-base-content/70">Implement Settings Revamp dark mode toggle</p>
            </div>
          </div>

          {/* Logs */}
          <div className="flex-1 flex flex-col min-h-0">
            <div className="flex items-center justify-between mb-0.5">
              <span className="text-[5px] font-semibold uppercase tracking-wider text-base-content/60">Logs</span>
            </div>
            <div className="flex-1 min-h-0 rounded border border-base-200 bg-base-200/30 px-1.5 py-1 overflow-hidden space-y-1">
              {detailLogs.map((log, i) => (
                <div
                  key={log.time + log.msg}
                  className="text-[5px] leading-relaxed"
                  style={{ animation: 'detail-log-in 0.3s ease-out both', animationDelay: `${i * 120}ms` }}
                >
                  <div className="flex items-center gap-1">
                    <span className="font-mono text-base-content/35">{log.time}</span>
                    <span className={`rounded-full px-1 py-px text-[4px] font-semibold ${log.levelCls}`}>
                      {log.level}
                    </span>
                  </div>
                  <p className="font-mono text-base-content/60 mt-px">{log.msg}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Continue button */}
          <button
            type="button"
            className="w-full shrink-0 rounded-lg bg-primary py-1.5 text-[7px] font-semibold text-primary-content"
            tabIndex={-1}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── Main animated component ─── */

export function AnimatedPhoneMockup({ className = '' }: { className?: string }) {
  const [step, setStep] = useState<AnimStep>('list');
  const [typingState, setTypingState] = useState<TypingState>(initialTypingState);
  const [newItemStatus, setNewItemStatus] = useState<'Pending' | 'Running' | 'Done'>('Pending');
  const [filledFields, setFilledFields] = useState<Set<SelectFieldId>>(new Set());
  const [optionsChecked, setOptionsChecked] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const typingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const mountedRef = useRef(true);

  const clearTimers = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (typingRef.current) {
      clearInterval(typingRef.current);
      typingRef.current = null;
    }
  }, []);

  const delay = useCallback(
    (ms: number): Promise<void> =>
      new Promise((resolve) => {
        timerRef.current = setTimeout(() => {
          if (mountedRef.current) resolve();
        }, ms);
      }),
    [],
  );

  const typeChars = useCallback(
    (text: string, updater: (char: string) => void): Promise<void> =>
      new Promise((resolve) => {
        let idx = 0;
        typingRef.current = setInterval(() => {
          if (!mountedRef.current) {
            clearInterval(typingRef.current!);
            return;
          }
          idx++;
          updater(text.slice(0, idx));
          if (idx >= text.length) {
            clearInterval(typingRef.current!);
            typingRef.current = null;
            resolve();
          }
        }, TYPING_SPEED_MS);
      }),
    [],
  );

  const runTypingSequence = useCallback(
    async (cancelled: () => boolean) => {
      for (const segment of PROMPT_SEGMENTS) {
        if (cancelled()) return;

        if (segment.type === 'text') {
          // Type normal text
          await typeChars(segment.value, (partial) => {
            setTypingState((prev) => ({ ...prev, currentText: partial, phase: 'typing-text' }));
          });
          if (cancelled()) return;
          // Commit text to rendered
          setTypingState((prev) => ({
            ...prev,
            rendered: [...prev.rendered, prev.currentText],
            currentText: '',
          }));
        } else {
          // Type slash command char by char
          await typeChars(segment.command, (partial) => {
            setTypingState((prev) => ({ ...prev, slashText: partial, phase: 'typing-slash' }));
          });
          if (cancelled()) return;

          // Show slash menu
          await delay(DELAYS.slashMenuAppear);
          if (cancelled()) return;
          setTypingState((prev) => ({
            ...prev,
            showSlashMenu: true,
            slashMenuItems: segment.menuItems,
            slashSelectedIndex: segment.selectedIndex,
            phase: 'slash-menu-visible',
          }));

          // Wait then select
          await delay(DELAYS.slashMenuSelect);
          if (cancelled()) return;

          // Insert chip, clear slash text, hide menu
          setTypingState((prev) => ({
            ...prev,
            rendered: [
              ...prev.rendered,
              <EntityChip key={`chip-${prev.rendered.length}`} icon={segment.chip.icon} label={segment.chip.label} />,
            ],
            slashText: '',
            showSlashMenu: false,
            slashMenuItems: [],
            slashSelectedIndex: -1,
            phase: 'slash-menu-selected',
          }));
          await delay(150);
        }
      }
      setTypingState((prev) => ({ ...prev, phase: 'done' }));
    },
    [typeChars, delay],
  );

  useEffect(() => {
    mountedRef.current = true;
    let cancelled = false;

    const run = async () => {
      while (!cancelled && mountedRef.current) {
        // 1. Show list
        setStep('list');
        setTypingState(initialTypingState);
        setNewItemStatus('Pending');
        setFilledFields(new Set());
        setOptionsChecked(false);
        await delay(DELAYS.initialWait);
        if (cancelled) break;

        // 2. FAB pulse
        setStep('fab-pulse');
        await delay(800);
        if (cancelled) break;

        // 3. Open modal
        setStep('modal-open');
        await delay(DELAYS.afterModalOpen);
        if (cancelled) break;

        // 4. Sequential select fill
        for (const field of selectFields) {
          if (cancelled) break;
          setStep(selectStepMap[field.id]);
          await delay(DELAYS.selectInterval);
          if (cancelled) break;
          setFilledFields((prev) => new Set([...prev, field.id]));
        }
        if (cancelled) break;
        await delay(DELAYS.afterSelects);
        if (cancelled) break;

        // 5. Check Plan Mode + Worktree
        setStep('check-options');
        await delay(350);
        if (cancelled) break;
        setOptionsChecked(true);
        await delay(300);
        if (cancelled) break;

        // 6. Typing with slash command
        setStep('typing');
        await runTypingSequence(() => cancelled);
        if (cancelled) break;
        await delay(DELAYS.afterTypingDone);
        if (cancelled) break;

        // 6. Sending
        setStep('sending');
        await delay(600);
        if (cancelled) break;

        // 7. Close modal
        setStep('modal-close');
        await delay(DELAYS.afterModalClose);
        if (cancelled) break;

        // 8. New item enters
        setStep('new-item-enter');
        setNewItemStatus('Pending');
        await delay(DELAYS.pendingToRunning);
        if (cancelled) break;

        // 9. Pending → Running
        setStep('status-running');
        setNewItemStatus('Running');
        await delay(DELAYS.runningToDone);
        if (cancelled) break;

        // 10. Running → Done
        setStep('status-done');
        setNewItemStatus('Done');
        await delay(DELAYS.afterDone);
        if (cancelled) break;

        // 11. Tap row → detail view slides in
        setStep('detail-enter');
        await delay(350);
        if (cancelled) break;

        // 12. Show detail view
        setStep('detail-view');
        await delay(DELAYS.detailView);
        if (cancelled) break;
      }
    };

    void run();

    return () => {
      cancelled = true;
      mountedRef.current = false;
      clearTimers();
    };
  }, [delay, runTypingSequence, clearTimers]);

  const isModalStep =
    step === 'modal-open' ||
    step === 'typing' ||
    step === 'sending' ||
    step === 'select-runner' ||
    step === 'select-agent' ||
    step === 'select-engine' ||
    step === 'select-model' ||
    step === 'check-options';
  const showNewItem =
    step === 'new-item-enter' ||
    step === 'status-running' ||
    step === 'status-done' ||
    step === 'detail-enter' ||
    step === 'detail-view' ||
    step === 'cooldown';
  const showDetail = step === 'detail-enter' || step === 'detail-view';
  const fabPulsing = step === 'fab-pulse';
  const requestCount = showNewItem ? existingRows.length + 1 : existingRows.length;
  const currentStatusConfig = statusConfig[newItemStatus] ?? statusConfig.Pending;

  const activeField: SelectFieldId | null =
    step === 'select-runner'
      ? 'runner'
      : step === 'select-agent'
        ? 'agent'
        : step === 'select-engine'
          ? 'engine'
          : step === 'select-model'
            ? 'model'
            : null;

  // 축소된 제품 목업이다. 200px 폭 안에 앱 화면 전체를 그리므로 글자 크기·여백·카드 중첩은
  // 사용자가 읽는 값이 아니라 그림의 축척이다. impeccable 검출기는 "UI의 그림"과 "UI"를
  // 구분하지 못하므로 축척에 종속된 룰만 면제한다. 색·대비·모션처럼 축소해도 그대로 보이는
  // 룰은 계속 적용받는다.
  return (
    <div
      className={`w-[200px] ${className}`}
      style={{ aspectRatio: '9/17' }}
      data-impeccable-ignore="undersized-ui-text tiny-text cramped-padding nested-cards"
    >
      <style>{`
        @keyframes fab-pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.15); }
        }
        @keyframes cursor-blink {
          0%, 100% { opacity: 1; }
          50% { opacity: 0; }
        }
        @keyframes slide-in-row {
          from { opacity: 0; max-height: 0; padding-top: 0; padding-bottom: 0; }
          to { opacity: 1; max-height: 60px; padding-top: 0.75rem; padding-bottom: 0.75rem; }
        }
        @keyframes slash-menu-in {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes detail-log-in {
          from { opacity: 0; transform: translateY(2px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <div className="flex h-full flex-col rounded-[32px] border-[3px] border-base-content/15 bg-base-300/40 p-1.5 shadow-lg">
        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-[24px] bg-base-100">
          <StatusBar />
          <AppHeader count={requestCount} />

          <div className="flex-1 divide-y divide-base-200/60 overflow-hidden">
            {showNewItem && (
              <div
                className={`transition-colors duration-200 ${step === 'detail-enter' ? 'bg-base-200/60' : ''}`}
                style={{ animation: 'slide-in-row 0.4s ease-out forwards' }}
              >
                <RunnerRow
                  prompt="Implement Settings Revamp dark mode toggle"
                  runner="Mac Mini"
                  engine="Claude"
                  status={currentStatusConfig.label}
                  statusCls={currentStatusConfig.cls}
                  time="now"
                />
              </div>
            )}
            {existingRows.map((row) => (
              <RunnerRow key={row.prompt} {...row} />
            ))}
          </div>

          {!isModalStep && <FabButton pulsing={fabPulsing} />}

          <div className="mx-auto mb-1.5 mt-1 h-1 w-10 rounded-full bg-base-content/15" />

          <DetailBottomSheet visible={showDetail} />

          <ModalOverlay
            visible={isModalStep}
            filledFields={filledFields}
            activeField={activeField}
            optionsChecked={optionsChecked}
            typingState={typingState}
            sending={step === 'sending'}
          />
        </div>
      </div>
    </div>
  );
}
